import type { StaticEntityType } from "@config";
import type { TDesignSlide, TMasterSlide } from "@/shared/types";

// Shape of the exported slides-library JSON served from the static assets CDN
// (/assets/json/layout-source.json). Only the fields we consume are typed; the
// export carries more we ignore.
type RawFolder = {
  _id: string;
  title: string;
  parentId?: string;
  archived?: boolean;
  order?: number;
};

type RawSlide = {
  _id: string;
  title: string;
  slide: StaticEntityType;
  folderId?: string;
  archived?: boolean;
  isPopular?: boolean;
  isPro?: boolean;
  order?: number;
};

// Slides flagged `isPopular` are surfaced under this key, which the grid matches
// via `isMostPopularTitle` to fill the "Most popular" section. Kept separate
// from the (empty) "Most popular" folder so popular slides still live in their
// own category sections too.
export const MOST_POPULAR_KEY = "Most popular";

const isMostPopularFolder = (title: string) =>
  title.trim().toLowerCase() === MOST_POPULAR_KEY.toLowerCase();

export type RawLayoutsExport = {
  folderId?: string;
  folders?: RawFolder[];
  slides?: RawSlide[];
};

export type LayoutsData = {
  categories: TMasterSlide[];
  // Keyed by category (folder) `_id`, matching what `buildLayoutsSections`
  // expects from the backend layouts response.
  slides: Record<string, TDesignSlide[]>;
};

const toDesignSlide = (
  raw: RawSlide,
  categoryTitle?: string,
): TDesignSlide => ({
  _id: raw._id,
  title: raw.title,
  // Unused by the live-render preview path; kept to satisfy TDesignSlide.
  imageUrl: "",
  masterSlide: raw.folderId ?? "",
  content: raw.slide,
  // Source category name — badged on "Most popular" / "Recently used" cards
  // (other sections already convey the category via their section header).
  categoryTitle,
  // Curation flags from the global library export.
  isPopular: raw.isPopular,
  isPro: raw.isPro,
});

// Transforms the raw library export into the `{ categories, slides }` shape the
// Layouts grid + sidebar consume. Categories are the root folder's children
// ordered by `order` (the "Most popular" folder is excluded — it's rendered as a
// dedicated section); slides are grouped by their `folderId`, plus a
// `MOST_POPULAR_KEY` bucket holding every `isPopular` slide. Archived folders and
// slides are dropped.
export const transformLayoutsExport = (raw: RawLayoutsExport): LayoutsData => {
  const folders = raw.folders ?? [];
  const slides = raw.slides ?? [];

  // The root folder is the export's `folderId`; fall back to any folder whose
  // parent isn't itself a folder in the export.
  const folderIds = new Set(folders.map((f) => f._id));
  const rootId =
    raw.folderId ??
    folders.find((f) => !f.parentId || !folderIds.has(f.parentId))?._id;

  const categories = folders
    .filter(
      (f) =>
        f.parentId === rootId && !f.archived && !isMostPopularFolder(f.title),
    )
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const slidesByFolder: Record<string, TDesignSlide[]> = {};
  for (const category of categories) slidesByFolder[category._id] = [];

  const categoryTitleById = new Map(categories.map((c) => [c._id, c.title]));

  const popular: { slide: TDesignSlide; order: number }[] = [];

  for (const slide of slides) {
    if (slide.archived) continue;
    const categoryTitle = categoryTitleById.get(slide.folderId ?? "");
    // Only slides that land in a visible (non-archived, included) category are
    // eligible — so a popular slide whose folder is archived or excluded (e.g.
    // the "Most popular" folder) doesn't leak into the Most popular section.
    const bucket = slide.folderId && slidesByFolder[slide.folderId];
    if (!bucket) continue;
    if (slide.isPopular) {
      popular.push({
        slide: toDesignSlide(slide, categoryTitle),
        order: slide.order ?? 0,
      });
    }
    bucket.push(toDesignSlide(slide, categoryTitle));
  }

  popular.sort((a, b) => a.order - b.order);

  return {
    categories: categories.map((f) => ({ _id: f._id, title: f.title })),
    slides: {
      ...slidesByFolder,
      ...(popular.length > 0
        ? { [MOST_POPULAR_KEY]: popular.map((p) => p.slide) }
        : {}),
    },
  };
};
